/* eslint-disable no-param-reassign */
import {
  StateCreator, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
// import { setUserInfo, useOfficialStore } from '$/store/useOfficialStore';
// import { message } from '$/components/Message';
// import { addBroadcastListener, sendBroadcastMessage } from '$/common/utils/broadcastChannel';
import {
  setUserInfo, useOfficialStore,
} from '$/store/useOfficialStore';
import { message } from '$/components/Message';
import { addBroadcastListener, sendBroadcastMessage } from '$/common/utils/broadcastChannel';
import { getRecordList } from '$/domains/record/recordService';
import { isMobile } from '$/utils';
import { getPopupInfo } from '$/api/canvasRquest';
import { resetSubscribe } from '$/common/utils/subscribeUtil';
import { loggleVipModal } from '$/store/useVipModalStore';
import { createDefaultDeviceInfo } from '@meitu/catalyze';
import * as Sentry from '@sentry/react';
import {
  createDefaultUser, getAccessToken, getUID, getUserInfo, loginPopup, logoutPopup, removeAccessToken, removeUID,
} from '@/common/utils/accountUtil';
import useTeamStore, { clearTeamInfo, refreshTeamInfo } from '@/store/useTeamStore';
import { getGid } from '@/utils';
import { Config } from '@/common/config';
import type { User } from '$/store/useOfficialStore/types';
import {
  CommonStoreType,
} from './types';

/** 将任意异常转换为普通字符串，避免日志包装器在对象转字符串时二次抛错。 */
function safeStringifyError(error: unknown) {
  if (error instanceof Error) {
    return JSON.stringify({
      name: error.name,
      message: error.message,
      stack: error.stack,
    });
  }

  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error) || '[empty error]';
  } catch {
    return '[unserializable error]';
  }
}

/** 将当前账号、团队和设备标识同步到 Sentry，未登录时清除旧用户。 */
function syncSentryUser(userInfo: Pick<User, 'uid' | 'screenName'>) {
  if (!userInfo.uid) {
    Sentry.setUser(null);
    return;
  }

  const { teamInfo } = useTeamStore.getState();
  Sentry.setUser({
    uid: userInfo.uid,
    oid: teamInfo?.oid == null ? '' : String(teamInfo.oid),
    username: userInfo.screenName,
    gid: getGid(),
  });
}

const commonStoreSlice: StateCreator<CommonStoreType, [
  ['zustand/immer', never],
]> = (set, get) => ({
  initialized: false,

  device: createDefaultDeviceInfo(),

  isOnline: true,

  currentPathname: undefined,

  navbarExpand: false,
  v3CanvasPublishEnabled: false,
  v3CanvasPublishConfigLoaded: false,
  popupInfo: null,

  headerConfig: {
    showHeaderBackgroundColor: false, // 是否显示头部背景色
    showBlurBackground: true, // 是否显示模糊背景
    height: 60, // 头部高度
  },

  homeSeedanceGuideHeight: 0,

  /** 初始化通用登录态和入口配置，供页面级能力开关消费。 */
  async init() {
    addBroadcastListener('signIn', () => {
      const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
      if (!isUserLoggedIn) {
        get().initUserInfo();
        return;
      }
      refreshTeamInfo();
    });
    addBroadcastListener('signOut', async () => {
      const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
      if (isUserLoggedIn) {
        window.location.reload();
      }
    });
    await Promise.all([
      get().initUserInfo(),
      get().initPopupInfo(),
    ]);
    set({ initialized: true });
  },

  /** 获取入口通用配置，并将 v3 画布发布开关写入全局 store。 */
  async initPopupInfo() {
    try {
      const popupInfo = await getPopupInfo();
      set({
        popupInfo,
        v3CanvasPublishEnabled: Boolean(popupInfo?.v3_canvas_publish_enabled),
        v3CanvasPublishConfigLoaded: true,
      });
    } catch (e) {
      const error = safeStringifyError(e);
      console.error('initPopupInfo error', error);
      Sentry.captureMessage('initPopupInfo error', {
        level: 'warning',
        extra: {
          error,
        },
      });
      set({
        popupInfo: null,
        v3CanvasPublishEnabled: false,
        v3CanvasPublishConfigLoaded: true,
      });
    }
  },

  /** 初始化帐号信息并同步到 SDK 全局用户态，异常只上报不阻塞页面初始化。 */
  async initUserInfo() {
    try {
      let userInfo = createDefaultUser();
      const token = getAccessToken();
      const uid = getUID();
      if (token && uid) {
        const res = await getUserInfo();
        if (res && res.data.meta.code === 0) {
          const { phoneCc, phone } = res.data.response.user;
          userInfo = {
            uid: `${res.data.response.user.id}`,
            avatar: res.data.response.user.avatar,
            token: getAccessToken() || '',
            screenName: res.data.response.user.screenName,
            isChinaPhone: Number(phoneCc) === 86 && Boolean(phone),
          };
        } else {
          Sentry.captureMessage('initUserInfo else', {
            level: 'warning',
            extra: {
              error: JSON.stringify(res),
              userInfo,
            },
          });
        }
      }
      setUserInfo({
        uid: userInfo.uid,
        token: userInfo.token,
        screenName: userInfo.screenName,
        avatar: userInfo.avatar,
        isChinaPhone: userInfo.isChinaPhone,
      });
      const { loadSubscribeInfo } = useOfficialStore.getState();
      await loadSubscribeInfo();
      await refreshTeamInfo();
      syncSentryUser(userInfo);
    } catch (e) {
      const error = safeStringifyError(e);
      console.error('initUserInfo error', error);
      const { userInfo } = useOfficialStore.getState();
      Sentry.captureMessage('initUserInfo error', {
        level: 'warning',
        extra: {
          userInfo,
          error,
        },
      });
      // removeAccessToken();
      // removeUID();
    }
  },
  /** 登录新账号并废弃页面中可能残留的旧订阅支付上下文。 */
  async toLogin() {
    if (!get().isOnline) {
      message.error('RoboNeo_Web_network_fail_retry');
      return;
    }
    const { isOversea, userInfo } = useOfficialStore.getState();
    const result = await loginPopup({ isOversea: isOversea(), isMobile: isMobile() });
    if (result) {
      // 账号切换前先关闭购买弹窗并废弃旧账号的订阅 SDK 鉴权上下文。
      loggleVipModal(false);
      resetSubscribe();
      // HACK: 虽然大账号会写入 cookie，但 Chrome 116 之后 隐私沙盒限制，无法再跨域使用 cookie
      const nextUserInfo = {
        ...userInfo,
        uid: `${result.uid}`,
        token: result.accessToken,
        screenName: result.user.screenName,
        avatar: result.user.avatarHttps,
      };
      setUserInfo(nextUserInfo);

      // 统计身份同步失败不能阻断登录广播和团队初始化。
      try {
        window.mtstat?.login(`${result.uid}`);
      } catch {
        console.warn('[account] login tracking failed');
      }

      sendBroadcastMessage({ type: 'signIn', params: getAccessToken() });
      await refreshTeamInfo();
      syncSentryUser(nextUserInfo);
    }
  },

  // eslint-disable-next-line class-methods-use-this
  /** 退出当前账号并立即终止其订阅购买上下文。 */
  async toLogout() {
    // 登出时立即废弃可创建订单的弹窗和 SDK 实例，避免后续账号复用当前鉴权。
    loggleVipModal(false);
    resetSubscribe();
    logoutPopup();
    if (window.mtstat) {
      (window.mtstat as any).logout();
    }
    const userInfo = createDefaultUser();
    removeAccessToken();
    removeUID();
    setUserInfo({
      uid: userInfo.uid,
      token: userInfo.token,
      screenName: userInfo.screenName,
      avatar: userInfo.avatar,
      isChinaPhone: userInfo.isChinaPhone,
    });
    clearTeamInfo();
    syncSentryUser(userInfo);
    sendBroadcastMessage({ type: 'signOut' });
  },
  handleOnline: () => {
    set({
      isOnline: true,
    });
  },

  handleOffline: () => {
    set({
      isOnline: false,
    });
  },

  disposeOfflineListener() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('offline', get().handleOffline);
      window.removeEventListener('online', get().handleOnline);
    }
  },
  async getLatestRoomId() {
    const searchParams = new URLSearchParams(window.location.search);
    let roomId = searchParams.get('room_id') || '';
    if (!roomId) {
      const { list } = await getRecordList();
      if (list && list.length) {
        const latestItem = list[0];
        if (latestItem.items.length) {
          roomId = latestItem.items[0].room_id;
        }
      }
    }
    return roomId;
  },
});

export const useCommonStore = create<CommonStoreType>()(
  persist(
    immer(commonStoreSlice),
    {
      name: 'useCommonStore',
      version: 1,
      partialize: (state) => ({ navbarExpand: state.navbarExpand }),
    },
  ),
);

export const setCurrentPathname = (val: Config['Pathname']) => {
  useCommonStore.setState((state) => {
    state.currentPathname = val;
  });
};

/**
 * 设置侧边栏折叠
 *
 * @export
 * @param {boolean} val
 */
export function setNavbarExpand(val: boolean) {
  useCommonStore.setState((state) => {
    state.navbarExpand = val;
  });
}

/**
 * 设置header背景色
 *
 * @export
 * @param {CommonStoreType['headerConfig']} config
 */
export function setHeaderConfig(config: Partial<CommonStoreType['headerConfig']>) {
  useCommonStore.setState((state) => {
    state.headerConfig = {
      ...state.headerConfig,
      ...config,
    };
  });
}

/**
 * 设置首页 Seedance 引导条在布局中占用的垂直高度（px）。应由 `SeedanceGuide` 在挂载/卸载时分别设为实际高度与 0。
 *
 * @param {number} height
 */
export function setHomeSeedanceGuideHeight(height: number) {
  useCommonStore.setState((state) => {
    state.homeSeedanceGuideHeight = height;
  });
}
